import React, { useEffect, useMemo, useRef, useState } from "react";

function patchZohoUndefinedChunksOnce() {
  const w = window as any;
  if (w.__zohoChunkPatchInstalled) return;
  w.__zohoChunkPatchInstalled = true;

  const originalAppendChild = Element.prototype.appendChild;

  Element.prototype.appendChild = function <T extends Node>(node: T): T {
    try {
      const anyNode = node as any;

      if (
        anyNode?.tagName === "SCRIPT" &&
        typeof anyNode.src === "string" &&
        anyNode.src.includes("/zohoim/undefinedjs-chunks/")
      ) {
        anyNode.src = anyNode.src.replace(
          "/zohoim/undefinedjs-chunks/",
          "/zohoim/js-chunks/"
        );
      }
    } catch {}

    // important: return the same node type T
    return originalAppendChild.call(this, node) as T;
  };
}

declare global {
  interface Window {
    ZOHOIM?: ZohoIMGlobal;
  }
}

type ZohoIMGlobal = {
  widgetContainerId?: string;
  widgetLayout?: {
    showWidget?: boolean;
    needWidgetBubble?: boolean;
    needCloseIcon?: boolean;
  };
  widgetProps?: {
    contactInfo?: {
      name: string;
      email: string;
      phone: string;
    };
  };
  triggerEvent?: (eventName: string, payload?: any) => void;
};

export type ZohoBmLayoutOptions = {
  showWidget?: boolean;
  /**
   * Hide/show bubble icon at render time (Zoho naming sometimes differs across docs/versions).
   * We'll set both keys to be safe.
   */
  needWidgetBubble?: boolean;

  /** Close icon on the widget panel */
  needCloseIcon?: boolean;
};

export type ZohoBmWidgetProps = {
  /** Required: Zoho BM public channel id */
  channelId: string;

  /**
   * If you want the widget to render inside a specific container in your DOM.
   * Useful for custom placement.
   */
  widgetContainerId?: string;

  /**
   * Layout options passed to window.ZOHOIM.widgetLayout BEFORE script load.
   * You can override anything Zoho supports.
   */
  layout?: ZohoBmLayoutOptions;

  /**
   * Optional config passed to window.ZOHOIM.widgetConfig BEFORE script load.
   * Keep as loose record to allow any Zoho keys you need.
   */
  config?: Record<string, unknown>;

  /**
   * Visitor identity + custom attributes.
   * Recommended keys: userId, email, name. You can add anything else.
   */
  visitor?: {
    name: string;
    email: string;
    phone: string;
  };

  /**
   * Called when the widget API is available.
   * Provides a small, typed facade over window.ZOHOIM.
   */
  onReady?: (api: ZohoBmApi) => void;

  /** Called if the script fails to load */
  onError?: (err: Error) => void;

  /**
   * If you want to control loading yourself, set to false and call api.load() manually via ref.
   * Default true.
   */
  autoLoad?: boolean;

  /**
   * Optional: allow passing a CSP nonce for the injected script tag.
   */
  scriptNonce?: string;
};

export type ZohoBmApi = {
  isReady: () => boolean;
  load: () => Promise<void>;

  open: () => void;
  close: () => void;

  showBubble: () => void;
  hideBubble: () => void;

  trigger: (eventName: string, payload?: any) => void;
};

function buildScriptUrl(channelId: string) {
  // Zoho BM widget script format:
  // https://im.zoho.com/api/v1/public/channel/<CHANNEL_ID>/widget
  return `https://im.zoho.com/api/v1/public/channel/${encodeURIComponent(
    channelId
  )}/widget`;
}

function getZoho(): ZohoIMGlobal | undefined {
  return typeof window !== "undefined" ? window.ZOHOIM : undefined;
}

function ensureZohoRoot() {
  if (typeof window === "undefined") return;
  window.ZOHOIM = window.ZOHOIM || {};
}

/**
 * Loads the widget script once per channelId.
 * Uses a stable DOM id, so multiple mounts won't duplicate scripts.
 */
async function loadZohoScript(
  channelId: string,
  nonce?: string
): Promise<void> {
  if (typeof document === "undefined") return;

  patchZohoUndefinedChunksOnce();

  (window as any).__webpack_public_path__ =
    "https://static.zohocdn.com/zohoim/";

  const scriptId = `zoho-bm-widget-${channelId}`;
  const existing = document.getElementById(
    scriptId
  ) as HTMLScriptElement | null;
  if (existing) {
    // Already in DOM; still need to wait until runtime API appears
    return;
  }

  const script = document.createElement("script");
  script.id = scriptId;
  script.src = buildScriptUrl(channelId);
  script.async = true;
  script.defer = true;
  if (nonce) script.setAttribute("nonce", nonce);

  const p = new Promise<void>((resolve, reject) => {
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Zoho BM widget script"));
  });

  document.head.appendChild(script);
  await p;
}

/**
 * Waits until the widget runtime API is available.
 */
async function waitForZohoApi(timeoutMs = 15000): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const z = getZoho();
    if (z?.triggerEvent) return;
    await new Promise((r) => setTimeout(r, 50));
  }

  const z = getZoho();
  console.error("[Zoho timeout] window.ZOHOIM keys:", z ? Object.keys(z) : z);
  console.error("[Zoho timeout] window.ZOHOIM value:", z);
  throw new Error(
    "Zoho BM widget loaded, but API did not become ready in time"
  );
}

export const ZohoBmWidget: React.FC<ZohoBmWidgetProps> = ({
  channelId,
  widgetContainerId,
  layout,
  config,
  visitor,
  onReady,
  onError,
  autoLoad = true,
  scriptNonce,
}) => {
  const [ready, setReady] = useState(false);
  const loadedRef = useRef(false);

  // Build a facade over window.ZOHOIM for convenience
  const api: ZohoBmApi = useMemo(
    () => ({
      isReady: () => {
        const z = getZoho();
        return Boolean(z?.triggerEvent);
      },

      load: async () => {
        if (loadedRef.current) return;
        loadedRef.current = true;

        // 1) Define config BEFORE script loads
        ensureZohoRoot();
        const z = getZoho()!;

        if (widgetContainerId) z.widgetContainerId = widgetContainerId;

        z.widgetLayout = { ...(layout || {}) };

        // ✅ ensure widgetProps exists + set visitor BEFORE script loads
        if (visitor) {
          z.widgetProps = z.widgetProps ?? {};
          z.widgetProps.contactInfo = visitor;
        }

        // 2) Load script, then wait for runtime API
        await loadZohoScript(channelId, scriptNonce);
        await waitForZohoApi();

        // 3) Optionally hide bubble again after ready
        if (!layout?.needWidgetBubble) {
          try {
            z.triggerEvent?.("hideBubble");
          } catch {}
        }

        setReady(true);
        onReady?.(api);
      },

      open: () => getZoho()?.triggerEvent?.("openWidget"),
      close: () => getZoho()?.triggerEvent?.("closeWidget"),

      showBubble: () => getZoho()?.triggerEvent?.("showBubble"),
      hideBubble: () => getZoho()?.triggerEvent?.("hideBubble"),

      trigger: (eventName: string, payload?: any) =>
        getZoho()?.triggerEvent?.(eventName, payload),
    }),
    [channelId, config, layout, onReady, scriptNonce, visitor]
  );

  // Auto-load on mount
  useEffect(() => {
    if (!autoLoad) return;
    api.load().catch((e) => {
      setReady(false);
      onError?.(e instanceof Error ? e : new Error(String(e)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, channelId]);

  // Update visitor info when props.visitor changes (after ready)
  useEffect(() => {
    if (!ready) return;
    // if (visitor) api.setVisitor(visitor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, JSON.stringify(visitor || {})]);

  return null;
};
